import { useCallback, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { LOYALTY_PURCHASE_POINTS_SUCCESS_SESSION_KEY } from 'loyalty/constants/loyaltyPurchasePoints'
import { useAppDispatch } from 'hooks/reduxHooks'
import fetchLoyaltyAccountActivity from 'actions/loyalty/fetchLoyaltyAccountActivity'
import { refreshLoyaltyPurchasePointsOffers } from 'actions/loyalty/fetchLoyaltyPurchasePointsOffers'
import requestLoyaltyAccount from 'api/loyalty/requestLoyaltyAccount'
import { API_CALL } from 'actions/actionConstants'
import { FETCH_LATEST_LOYALTY_ACCOUNT_DETAILS } from 'actions/apiActionConstants'
import { LOYALTY_ACTIVITIES_DEFAULT_END_DATE, LOYALTY_ACTIVITIES_DEFAULT_START_DATE } from 'loyalty/constants/loyaltyConfig'

interface SuccessPayload {
  creditedPoints?: unknown
}

const REFRESH_RETRY_DELAY_MS = 2000

/**
 * Reads + clears the sessionStorage handoff written by
 * `getOnPurchaseSuccessPath` after a successful Société Buy Points checkout
 * and runs the post-purchase side-effects on the destination page:
 *
 *   1. Surfaces a one-shot success snackbar.
 *   2. Re-fetches the loyalty account (balance) and account activity feed,
 *      bypassing the redux cache so the hero balance and activity timeline
 *      reflect the new purchase.
 *   3. Re-dispatches the same refetches once after a short delay, since
 *      svc-order's confirmation isn't strictly synchronous with the redirect
 *      — the immediate fetch covers the fast-BE case, the delayed retry
 *      covers the slow tail.
 *
 * Runs once per mount; subsequent navigations to /societe see an empty key.
 *
 * Mirrors `useSharedCheckoutSnackbar`'s sessionStorage handoff pattern.
 */
function useOnLoyaltyPurchasePointsSuccess() {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const hasShown = useRef(false)

  const refreshLoyaltyData = useCallback(() => {
    dispatch(fetchLoyaltyAccountActivity(LOYALTY_ACTIVITIES_DEFAULT_START_DATE, LOYALTY_ACTIVITIES_DEFAULT_END_DATE, 1, 1))
    dispatch({
      type: API_CALL,
      api: FETCH_LATEST_LOYALTY_ACCOUNT_DETAILS,
      request: () => requestLoyaltyAccount(),
    })
    dispatch(refreshLoyaltyPurchasePointsOffers())
  }, [dispatch])

  useEffect(() => {
    if (hasShown.current) return

    let raw: string | null = null
    try {
      raw = sessionStorage.getItem(LOYALTY_PURCHASE_POINTS_SUCCESS_SESSION_KEY)
      sessionStorage.removeItem(LOYALTY_PURCHASE_POINTS_SUCCESS_SESSION_KEY)
    } catch {
      return
    }
    if (!raw) return

    let payload: SuccessPayload
    try {
      payload = JSON.parse(raw)
    } catch {
      return
    }

    const creditedPoints = typeof payload.creditedPoints === 'number' ? payload.creditedPoints : 0
    if (creditedPoints <= 0) return

    hasShown.current = true
    showSnackbar(
      intl.formatMessage(
        {
          id: 'LrhSctasQJO4z2IYqFwP',
          defaultMessage: '{points, number} points have been added to your Société account',
          description: 'Snackbar shown on the Société account landing page after a successful points purchase, including the credited point amount',
        },
        { points: creditedPoints },
      ),
      'success',
    )

    refreshLoyaltyData()
    const timer = window.setTimeout(refreshLoyaltyData, REFRESH_RETRY_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [intl, refreshLoyaltyData])
}

export default useOnLoyaltyPurchasePointsSuccess
